import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Column, ConfirmationModal, TextField, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useConnectStanfordTax } from '~/hooks/reactQuery/mutations/useConnectStanfordTax';
import { useLocaleKeys } from '~/i18n';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { classifyStanfordTaxConnectFailure, StanfordTaxConnectFailureSurface, StanfordTaxConnectFieldError } from './ConnectStanfordTaxModal.utils';
const EXAMPLE_FIRM_IDENTIFIER = 'pinewood-accounting';
export const GetConnectStanfordTaxModalTID = (parentTestId)=>getTestIds(parentTestId);
export const ConnectStanfordTaxModal = (param)=>{
    let { dismissModal, registerCanDismiss } = param;
    const { t: stanfordTaxTexts } = useLocaleKeys('integrations.setup.stanfordTax');
    const { t: sharedTexts } = useLocaleKeys('integrations.apps.shared');
    const { t: commonTexts } = useLocaleKeys('common');
    const { ConnectStanfordTaxModalTID } = useMemo(()=>GetConnectStanfordTaxModalTID(), []);
    const { control, handleSubmit, setError } = useForm({
        mode: 'all',
        defaultValues: {
            firmIdentifier: ''
        }
    });
    const { showErrorToast } = useOverlayContext();
    const { connectStanfordTax, isLoading } = useConnectStanfordTax();
    const fieldErrorTexts = {
        [StanfordTaxConnectFieldError.IdentifierMalformed]: stanfordTaxTexts.firmIdentifierMalformed({
            example: EXAMPLE_FIRM_IDENTIFIER
        }),
        [StanfordTaxConnectFieldError.FirmNotFound]: stanfordTaxTexts.firmNotFound(),
        [StanfordTaxConnectFieldError.FirmNotActive]: stanfordTaxTexts.firmNotActive()
    };
    const presentFailure = (error)=>{
        const failure = classifyStanfordTaxConnectFailure(error);
        if (failure.surface === StanfordTaxConnectFailureSurface.Field) {
            setError('firmIdentifier', {
                message: fieldErrorTexts[failure.reason]
            });
        } else {
            showErrorToast(stanfordTaxTexts.connectTryAgain(), ConnectStanfordTaxModalTID.TryAgainToast());
        }
    };
    const submit = (param)=>{
        let { firmIdentifier } = param;
        connectStanfordTax({
            firmIdentifier
        }, {
            onSuccess: ()=>dismissModal(undefined, {
                    force: true
                }),
            onError: presentFailure
        });
    };
    const submitForm = handleSubmit(submit);
    const connectOnEnter = (event)=>{
        if (event.key === 'Enter' && !isLoading) {
            submitForm();
        }
    };
    const renderFirmIdentifierField = (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
            bi: {
                name: 'stanford_tax_firm_identifier'
            },
            testId: ConnectStanfordTaxModalTID.FirmIdentifier(),
            label: stanfordTaxTexts.firmIdentifierLabel(),
            placeholder: EXAMPLE_FIRM_IDENTIFIER,
            inputProps: {
                onKeyDown: connectOnEnter
            }
        }, connectControllerToTextField(p)));
    const dismissUnlessConnecting = ()=>{
        if (!isLoading) {
            dismissModal();
        }
    };
    registerCanDismiss === null || registerCanDismiss === void 0 ? void 0 : registerCanDismiss(()=>Promise.resolve(!isLoading));
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'connect_stanford_tax_confirm'
            },
            secondaryButton: {
                name: 'connect_stanford_tax_cancel'
            },
            closeButton: {
                name: 'connect_stanford_tax_close'
            }
        },
        testId: ConnectStanfordTaxModalTID.Modal(),
        title: stanfordTaxTexts.connectTitle(),
        width: "640px",
        textAlignCenter: false,
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            alignItems: "stretch",
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    align: "left",
                    testId: ConnectStanfordTaxModalTID.Instructions(),
                    children: [
                        stanfordTaxTexts.connectInstructions(),
                        ' ',
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            href: helpArticles.stanfordTaxIntegration(),
                            bi: {
                                name: 'connect_stanford_tax_learn_more'
                            },
                            testId: ConnectStanfordTaxModalTID.LearnMore(),
                            children: commonTexts.buttons.learnMore()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    name: "firmIdentifier",
                    control: control,
                    rules: requiredValidation(stanfordTaxTexts.firmIdentifierRequired()),
                    render: renderFirmIdentifierField
                })
            ]
        }),
        primaryButtonLabel: sharedTexts.connectButton(),
        primaryButtonTestId: ConnectStanfordTaxModalTID.Submit(),
        primaryButtonLoading: isLoading,
        cancelButtonDisabled: isLoading,
        onPrimaryButtonClick: submitForm,
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        secondaryButtonTestId: ConnectStanfordTaxModalTID.Cancel(),
        onSecondaryButtonClick: dismissUnlessConnecting,
        onCloseButtonClick: dismissUnlessConnecting
    });
};
